import { useEffect, useState } from 'react'

import { cn } from '@/utils/style'

type DataAttributes = {
  [key: `data-${string}`]: string | number | undefined
}

interface RadioProps {
  disabled?: boolean
  checked?: boolean
  /** Shows a minus mark instead of a check; visual only, `checked` still drives state */
  indeterminate?: boolean
  className?: string
  inputClassName?: string
  children?: React.ReactNode
  onChange?: (checked: boolean) => void
  checkedBgColor?: string
  checkedBorderColor?: string
  checkIconColor?: string
  labelProps?: Omit<React.LabelHTMLAttributes<HTMLLabelElement>, 'className'> & DataAttributes
  inputProps?: Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    'type' | 'className' | 'disabled' | 'checked' | 'onChange'
  > &
    DataAttributes
}

export function Checkbox({
  disabled,
  checked,
  indeterminate = false,
  className,
  inputClassName,
  children,
  onChange,
  checkedBgColor,
  checkedBorderColor,
  checkIconColor,
  labelProps,
  inputProps,
}: RadioProps) {
  const [selected, setSelected] = useState(checked ?? false)

  useEffect(() => {
    setSelected(checked ?? false)
  }, [checked])

  const inputBaseClass = [
    'appearance-none',
    'shrink-0',
    'w-[18px] h-[18px]',
    'rounded-[4px]',
    'border-2',
    'border-neutral-300',
    'bg-panel-white',
    'relative',
    'transition',
    // 勾勾 (pseudo element) 預設隱藏
    'after:content-[""]',
    'after:absolute after:left-[28%] after:top-[6%]',
    'after:w-[45%] after:h-[75%]',
    'after:border-r-2 after:border-b-2 after:border-transparent',
    'after:scale-0 after:rotate-45 after:origin-left-top',
    'after:transition',
  ].join(' ')

  // 當 checkbox 被勾選且未 disabled 時，要額外套用的樣式
  const selectedClass =
    selected && !disabled
      ? cn(
          'after:scale-100', // 顯示勾勾
          checkIconColor ?? 'after:border-white', // 預設勾勾顏色，可覆寫
          checkedBgColor ?? 'bg-btn-secondary', // 勾選後底色
          checkedBorderColor ?? 'border-secondary', // 勾選後邊框，可覆寫
        )
      : ''

  // 半選（indeterminate）：把勾勾的 pseudo element 改成水平的「－」
  const indeterminateClass =
    indeterminate && !selected && !disabled
      ? cn(
          'after:scale-100 after:rotate-0 after:origin-center',
          'after:left-[25%] after:top-[calc(50%-2px)] after:w-[50%] after:h-0',
          'after:border-r-0 after:border-b-2',
          checkIconColor ?? 'after:border-white',
          checkedBgColor ?? 'bg-btn-secondary',
          checkedBorderColor ?? 'border-secondary',
        )
      : ''

  return (
    <label {...labelProps} className={cn('flex items-center gap-2', className)}>
      <input
        {...inputProps}
        type="checkbox"
        className={cn(
          inputBaseClass,
          disabled && 'bg-panel-gray-200 cursor-not-allowed ',
          selectedClass,
          indeterminateClass,
          inputClassName,
        )}
        disabled={disabled}
        checked={selected}
        onChange={(e) => {
          setSelected(e.target.checked)
          onChange && onChange(e.target.checked)
        }}
      />
      {children}
    </label>
  )
}
